// Parses the RFP `rateTable` field — the age-banded voluntary-life rate table
// (rate per $1,000 of coverage, by age band) that extraction stores as ONE string,
// one band per line: "0-24: EE $0.06 / SP $0.06", with an optional trailing
// "AD&D:" line. RFPProposals.tsx renders the result as a small grid inside a
// comparison cell (broker view and public share page use the same component).
//
// Split out of the page so it's testable — the page is .tsx and can't be imported
// from a node test.

export type RateBand = { band: string; cells: Record<string, string> };
export type ParsedRateTable = { labels: string[]; bands: RateBand[] };

// A band label is "0-24", "<20", "70+", or the trailing "AD&D" line. Used both to
// split a run-together single line and to tell a real band from a header/divider.
const BAND_LABEL_SOURCE = String.raw`<\s*\d+|\d+\s*[-–]\s*\d+|\d+\s*\+|AD&D`;
const BAND_LABEL = new RegExp(`^(?:${BAND_LABEL_SOURCE})$`, "i");
// The lookbehind matters: without it "70-74: EE $2.260 75-99:" also splits INSIDE
// the numbers ("7" + "0-74: …"), because "0-74:" matches the band pattern too. A
// band can only start where the previous character isn't part of a number.
const BAND_START = new RegExp(`(?<![\\d.$])(?=(?:${BAND_LABEL_SOURCE})\\s*:)`);

export function parseRateTable(raw: string): ParsedRateTable | null {
  // Prefer real line breaks. Values stored before the canonicalize `tidy` fix had
  // their newlines collapsed to spaces, so fall back to splitting before each band
  // label — that way old proposals still display without a re-analyze.
  const lines = raw.includes("\n") ? raw.split("\n") : raw.split(BAND_START);

  const labels: string[] = [];
  const bands: RateBand[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    const colon = trimmed.indexOf(":");
    if (colon === -1) continue; // header row / "---" divider from the regex fallback
    const band = trimmed.slice(0, colon).trim();
    // Skip anything that isn't a band, don't abandon the table. Extraction
    // prefixes these with a caption ("Age-banded rates:"), and because that line
    // HAS a colon it used to hit a bail-out and discard every band after it —
    // which is how a voluntary-life plan ended up converted with no rates at all.
    // Text that is genuinely not a rate table still yields null, via the
    // bands.length check at the end.
    if (!BAND_LABEL.test(band)) continue;
    const cells: Record<string, string> = {};
    for (const part of trimmed.slice(colon + 1).split("/")) {
      // "EE $0.06" → label "EE", value "$0.06". Unlabelled values key as "".
      const match = part.trim().match(/^([A-Za-z&+ ]+?)\s+(\S.*)$/);
      const [label, value] = match ? [match[1].trim(), match[2].trim()] : ["", part.trim()];
      if (!value) continue;
      if (!labels.includes(label)) labels.push(label);
      cells[label] = value;
    }
    bands.push({ band, cells });
  }
  return bands.length ? { labels, bands } : null;
}
